import type { Course } from '@/types/domain';

/* Bilingual ED course catalog. `cover` is a pastel band color used on cards. */
export const courses: Course[] = [
  {
    id: 'acls',
    code: 'ACLS-204',
    title: { en: 'Advanced Cardiac Life Support — AHA 2025', th: 'อบรมการกู้ชีพขั้นสูงตามแนวทาง AHA 2025 (ประจำปี 2026)' },
    host: 'ER Team',
    type: 'hybrid',
    status: 'active',
    cover: '#ffd8d0',
    hours: 12,
    capacity: 40,
    enrolled: 31,
    passScore: 80,
    certYears: 2,
    summary:
      'Team-based resuscitation: rhythm recognition, drug protocols and a hands-on megacode workshop.',
    requirements: ['lessons', 'attend', 'posttest'],
    shuffleQuestions: true,
    shuffleChoices: true,
    pretestRevealAnswers: true,
    lessons: [
      {
        id: 'acls-1', order: 1, title: { en: 'Course orientation', th: 'ปฐมนิเทศ' }, minutes: 8, kind: 'video', done: false,
        content: { type: 'html', files: ['sample-acls-handout.html'], note_en: 'Mock handout viewer sample. Production lessons use owner-uploaded HTML files from Supabase Storage.', note_th: 'ตัวอย่างสำหรับทดสอบตัวอ่านเอกสารในโหมด mock เท่านั้น บทเรียนจริงใช้ไฟล์ HTML ที่อัปโหลดใน Supabase Storage' },
        objective: { en: 'Understand how the course runs and what earns the certificate.', th: 'เข้าใจรูปแบบการเรียนและเกณฑ์การได้รับใบรับรอง' },
        keyPoints: [
          { en: 'Lessons, seminar and post-test are all required', th: 'ต้องเรียน เข้าร่วมสัมมนา และทำแบบทดสอบครบ' },
          { en: 'Certificate is valid for 2 years', th: 'ใบรับรองมีอายุ 2 ปี' },
        ],
      },
      {
        id: 'acls-2', order: 2, title: { en: 'Rhythm recognition', th: 'การอ่านคลื่นหัวใจ' }, minutes: 22, kind: 'video', done: false,
        videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
        objective: { en: 'Recognise shockable vs non-shockable rhythms and the priority for each.', th: 'แยกจังหวะที่ช็อกได้และช็อกไม่ได้ พร้อมลำดับการรักษา' },
        keyPoints: [
          { en: 'VF / pulseless VT → defibrillate immediately', th: 'VF / pulseless VT → ช็อกไฟฟ้าทันที' },
          { en: 'PEA / asystole → CPR + treat reversible causes', th: 'PEA / asystole → CPR และแก้ไขสาเหตุที่กลับคืนได้' },
          { en: 'Confirm the rhythm before any shock', th: 'ยืนยันจังหวะก่อนช็อกทุกครั้ง' },
        ],
      },
      {
        id: 'acls-3', order: 3, title: { en: 'Pharmacology of the arrest', th: 'ยาในภาวะหัวใจหยุดเต้น' }, minutes: 18, kind: 'reading', done: false,
        body: {
          en: 'Adrenaline (epinephrine) 1 mg IV/IO is given every 3–5 minutes throughout the arrest, regardless of rhythm. For VF or pulseless VT that persists after defibrillation, give amiodarone 300 mg IV/IO bolus, then a single 150 mg dose if needed. Always flush and continue high-quality compressions around every drug.',
          th: 'ให้อะดรีนาลีน 1 มก. ทางหลอดเลือดดำหรือกระดูก ทุก 3–5 นาทีตลอดการกู้ชีพ โดยไม่ขึ้นกับจังหวะหัวใจ สำหรับ VF หรือ pulseless VT ที่ยังคงอยู่หลังช็อกไฟฟ้า ให้อะมิโอดาโรน 300 มก. ทางหลอดเลือดดำ/กระดูก แล้วให้ซ้ำได้อีก 150 มก. หนึ่งครั้งหากจำเป็น ฟลัชสายและกดหน้าอกอย่างมีคุณภาพต่อเนื่องเสมอ',
        },
        attachmentUrl: '#', attachmentLabel: 'ACLS drug reference card · PDF',
        objective: { en: 'Know the dose, timing and indication for adrenaline and amiodarone.', th: 'ทราบขนาด เวลา และข้อบ่งชี้ของ adrenaline และ amiodarone' },
        keyPoints: [
          { en: 'Adrenaline 1 mg every 3–5 minutes', th: 'Adrenaline 1 มก. ทุก 3–5 นาที' },
          { en: 'Amiodarone 300 mg for refractory VF / VT', th: 'Amiodarone 300 มก. สำหรับ VF / VT ที่ดื้อต่อการรักษา' },
        ],
      },
      {
        id: 'acls-4', order: 4, title: { en: 'Megacode walkthrough', th: 'การจำลองสถานการณ์' }, minutes: 26, kind: 'video', done: false,
        videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        objective: { en: 'Run a full arrest as team leader, integrating rhythm and drugs.', th: 'นำทีมกู้ชีพเต็มรูปแบบ ผสานการอ่านจังหวะและการให้ยา' },
        keyPoints: [
          { en: 'Assign clear roles and call them out', th: 'มอบหมายบทบาทให้ชัดเจนและสื่อสารออกเสียง' },
          { en: 'Minimise interruptions to compressions', th: 'ลดการหยุดกดหน้าอกให้น้อยที่สุด' },
          { en: 'Reassess rhythm every 2 minutes', th: 'ประเมินจังหวะซ้ำทุก 2 นาที' },
        ],
      },
      {
        id: 'acls-5', order: 5, title: { en: 'Post-resuscitation care', th: 'การดูแลหลังกู้ชีพ' }, minutes: 15, kind: 'reading', done: false,
        objective: { en: 'Stabilise and hand over the patient after return of circulation.', th: 'ดูแลให้คงที่และส่งต่อผู้ป่วยหลังกลับมามีชีพจร' },
        keyPoints: [
          { en: 'Target oxygenation and blood pressure', th: 'ควบคุมออกซิเจนและความดันโลหิตตามเป้าหมาย' },
          { en: 'Find and treat the cause; hand over clearly', th: 'หาสาเหตุและรักษา พร้อมส่งต่ออย่างชัดเจน' },
        ],
      },
    ],
    enrollStart: '15 Jun 2026',
    enrollEnd: '26 Jun 2026 · 08:00',
    seminar: '26 Jun 2026 · 08:30–15:30',
    seminarDate: '2026-06-26',
    venue: 'Simulation Hall 2',
  },
  {
    id: 'ipc',
    code: 'IPC-110',
    title: { en: 'Infection Prevention & Control', th: 'การป้องกันและควบคุมการติดเชื้อ' },
    host: 'Infection Control',
    type: 'online',
    status: 'active',
    cover: '#cfe4ff',
    hours: 4,
    capacity: 300,
    enrolled: 188,
    passScore: 80,
    certYears: 1,
    summary:
      'Annual standard-precautions refresher: hand hygiene, PPE sequence and isolation principles.',
    requirements: ['lessons', 'posttest'],
    lessons: [
      { id: 'ipc-1', order: 1, title: { en: 'Chain of infection', th: 'วงจรการติดเชื้อ' }, minutes: 12, kind: 'video', done: true },
      { id: 'ipc-2', order: 2, title: { en: 'Five moments of hand hygiene', th: 'ห้าช่วงเวลาล้างมือ' }, minutes: 14, kind: 'video', done: true },
      { id: 'ipc-3', order: 3, title: { en: 'Donning & doffing PPE', th: 'การสวม–ถอด PPE' }, minutes: 16, kind: 'video', done: false },
      { id: 'ipc-4', order: 4, title: { en: 'Isolation precautions', th: 'การแยกผู้ป่วย' }, minutes: 10, kind: 'reading', done: false },
    ],
    enrollStart: '01 Jun',
    enrollEnd: '30 Jun',
    seminar: null,
    venue: null,
  },
  {
    id: 'med',
    code: 'MED-330',
    title: { en: 'Medication Safety', th: 'ความปลอดภัยด้านการบริหารยา' },
    host: 'Quality Improvement Team',
    type: 'online',
    status: 'active',
    cover: '#e4dcff',
    hours: 3,
    capacity: 250,
    enrolled: 96,
    passScore: 85,
    certYears: 1,
    summary: 'High-alert medications, the rights of administration and error-reporting culture.',
    requirements: ['lessons', 'posttest', 'eval'],
    lessons: [
      { id: 'med-1', order: 1, title: { en: 'High-alert medications', th: 'ยาความเสี่ยงสูง' }, minutes: 15, kind: 'video', done: false },
      { id: 'med-2', order: 2, title: { en: 'The rights of administration', th: 'หลักการบริหารยา' }, minutes: 12, kind: 'reading', done: false },
      { id: 'med-3', order: 3, title: { en: 'Reporting near-misses', th: 'การรายงานความคลาดเคลื่อน' }, minutes: 9, kind: 'reading', done: false },
    ],
    enrollStart: '01 Jun',
    enrollEnd: '15 Jul',
    seminar: null,
    venue: null,
  },
  {
    id: 'fire',
    code: 'SAF-101',
    title: { en: 'Fire Safety & Evacuation', th: 'การซ้อมอพยพและความปลอดภัยจากอัคคีภัย' },
    host: 'Risk Management',
    type: 'onsite',
    status: 'active',
    cover: '#ffe6bf',
    hours: 3,
    capacity: 60,
    enrolled: 54,
    passScore: 0,
    certYears: 1,
    summary: 'Annual onsite drill — extinguisher handling, evacuation routes and assembly points.',
    requirements: ['attend'],
    lessons: [],
    enrollStart: '05 Jun',
    enrollEnd: '24 Jun',
    seminar: '26 Jun 2026',
    venue: 'Auditorium A',
  },
  {
    id: 'pain',
    code: 'NUR-220',
    title: { en: 'Pain Assessment & Management', th: 'การประเมินและจัดการความปวด' },
    host: 'Nursing Education',
    type: 'hybrid',
    status: 'active',
    cover: '#c4eedd',
    hours: 6,
    capacity: 80,
    enrolled: 22,
    passScore: 80,
    certYears: 2,
    summary: 'Standardised pain scales, opioid stewardship and a bedside assessment workshop.',
    requirements: ['lessons', 'attend', 'posttest'],
    lessons: [
      { id: 'pain-1', order: 1, title: { en: 'Pain pathways', th: 'กลไกความปวด' }, minutes: 14, kind: 'video', done: false },
      { id: 'pain-2', order: 2, title: { en: 'Choosing a pain scale', th: 'การเลือกเครื่องมือ' }, minutes: 11, kind: 'reading', done: false },
    ],
    enrollStart: '10 Jun',
    enrollEnd: '05 Jul',
    seminar: '10 Jul 2026',
    venue: 'Nursing Lab 3',
  },
  {
    id: 'comm',
    code: 'QUA-140',
    title: { en: 'Communication for Patient Safety', th: 'การสื่อสารเพื่อความปลอดภัยผู้ป่วย' },
    host: 'Quality Improvement Team',
    type: 'online',
    status: 'draft',
    cover: '#ffd5ea',
    hours: 2,
    capacity: 200,
    enrolled: 0,
    passScore: 75,
    certYears: 1,
    summary: 'SBAR handover, closed-loop communication and speaking up for safety.',
    requirements: ['lessons', 'posttest'],
    lessons: [],
    enrollStart: '01 Jul',
    enrollEnd: '31 Jul',
    seminar: null,
    venue: null,
  },
];
